import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { Flex, Text } from "@/ui";
import AnimatedCheckIcon from "./AnimatedCheckIcon";
import AnimatedStepText from "./AnimatedStepText";
import { t } from "@/utils";

const loadingStepsKeys = [
  "applyingPreferences",
  "organizingFeatures",
  "optimizingStorage",
  "securingConnection"
] as const;

const FIRST_STEP_DELAY = 250;
const STEP_REVEAL_DELAY = 1500;
const TOTAL_SEQUENCE_DURATION = 6000;
const ROW_ANIMATION_DURATION = 0.3;

const loadingStepStartTimes = loadingStepsKeys.map((_, index) => FIRST_STEP_DELAY + index * STEP_REVEAL_DELAY);

export default function LoadingSteps({ onComplete }: { onComplete: Cb }) {
  const [visibleStepsCount, setVisibleStepsCount] = useState(0);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    const timeoutIds = loadingStepStartTimes.map((startTime, index) =>
      setTimeout(() => {
        setVisibleStepsCount(index + 1);
      }, startTime)
    );

    timeoutIds.push(
      setTimeout(() => {
        onCompleteRef.current();
      }, TOTAL_SEQUENCE_DURATION)
    );

    return () => {
      timeoutIds.forEach(clearTimeout);
    };
  }, []);

  return (
    <Flex w="fit-content" direction="column" gap="8" mt="16" minH="136">
      {loadingStepsKeys.map((step, index) => {
        const isVisible = index < visibleStepsCount;

        return (
          <motion.div
            key={step}
            initial={false}
            animate={{
              opacity: isVisible ? 1 : 0,
              y: isVisible ? 0 : 12
            }}
            transition={{ duration: ROW_ANIMATION_DURATION, ease: "easeInOut" }}
          >
            <Flex alignItems="center" columnGap="12" visibility={isVisible ? "visible" : "hidden"}>
              {isVisible ? <AnimatedCheckIcon /> : <Flex flexShrink={0} w="28px" h="28px" />}
              {isVisible ? (
                <AnimatedStepText text={t(step)} />
              ) : (
                <Text color="on-surface-variant" whiteSpace="pre">
                  {t(step)}
                </Text>
              )}
            </Flex>
          </motion.div>
        );
      })}
    </Flex>
  );
}
